import { castArray } from 'lodash-es';
import { find, merge, pick, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import {
    type DynamicTeaserElement,
    Element,
    ELEMENT_TYPES,
    type ParagraphElement,
} from '@@editor/helpers/Element';
import { isUnityDynamicTeaser, type UnityDynamicTeaser } from '@@editor/typings/UnityElements';
import addDefaultElementIfEmptyState from '@@editor/utils/addDefaultElementIfEmptyState';
import toParagraph from '@@editor/utils/toParagraph';

import { type ElementRule, type Next } from '../../types';
import deserializeCaption from '../utils/deserializeCaption';
import serializeCaption from '../utils/serializeCaption';
import { CURRENT_VERSION } from './../../constants';
import { createElementFromSingleLineState } from './../../deserializeNodes';

const deserializeTitle = (next: Next<ParagraphElement>) => (title: UnityDynamicTeaser['title']) =>
    pipe(
        title,
        addDefaultElementIfEmptyState,
        next,
        castArray,
        createElementFromSingleLineState(ELEMENT_TYPES.DYNAMIC_TEASER_TITLE),
    );

const serializeTitle =
    (next: Next<UnityDynamicTeaser['title']>) => (children: DynamicTeaserElement['children']) =>
        pipe(children, find(Element.isDynamicTeaserTitleElement), toParagraph, next);

type Rule = ElementRule<DynamicTeaserElement, UnityDynamicTeaser>;

const dynamicTeaserRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityDynamicTeaser(element)) {
            invariant(next, 'Cannot deserialize DynamicTeaser without a `next` function');

            const { image, title, type } = element;

            return {
                type,
                data: merge(pick(element, ['image', 'metadataIds', 'templateElement']), {
                    ...(image && {
                        image: {
                            ...image,
                            caption: deserializeCaption(next)(image?.caption),
                        },
                    }),
                }),
                children: [deserializeTitle(next)(title)],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isDynamicTeaserElement(node)) {
            invariant(next, 'Cannot serialize DynamicTeaser without a `next` function');

            const { children, data, type } = node;

            return {
                ...merge(pick(data, ['image', 'metadataIds', 'templateElement']), {
                    ...(data.image && {
                        image: {
                            ...data.image,
                            caption: serializeCaption(next)(data.image?.caption),
                        },
                    }),
                }),
                version: CURRENT_VERSION,
                type,
                title: serializeTitle(next)(children),
            };
        }
    },
};

export default dynamicTeaserRules;
